/**
 * Brazilian phone number utilities.
 *
 * Three representations coexist across the codebase:
 *   - E.164 ("+5511922220001")   — canonical for DB storage and equality.
 *   - National display ("(11) 92222-0001") — what users see in inputs/UI.
 *   - Digits-only ("11922220001") — intermediate, for mask building.
 *
 * Mobile: 11 digits (DDD + 9XXXXXXXX, leading 9 mandatory post-2016).
 * Fixo:   10 digits (DDD + 8XXXXXXX).
 */

/**
 * Strip every non-digit. Result is 0–13 chars (with country code prefix) or
 * 0–11 (without).
 */
export function cleanPhone(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Normalize to E.164 (+55…). Idempotent — re-running on already-normalized
 * input returns the same value.
 *
 * toE164("11922220001")       → "+5511922220001"
 * toE164("5511922220001")     → "+5511922220001"
 * toE164("+5511922220001")    → "+5511922220001"
 * toE164("(11) 92222-0001")   → "+5511922220001"
 * toE164("")                  → ""
 */
export function toE164(raw: string, countryCode = '55'): string {
  const digits = cleanPhone(raw);
  if (!digits) return '';
  if (digits.startsWith(countryCode)) return `+${digits}`;
  return `+${countryCode}${digits}`;
}

/**
 * Exact equality after E.164 normalization. No partial/suffix matching —
 * intentionally — to prevent collisions across users with similar tails.
 *
 * phonesMatch("11922220001", "+5511922220001") → true
 * phonesMatch("11922220001", "11999990001")    → false
 * phonesMatch("", "anything")                  → false
 */
export function phonesMatch(a: string, b: string): boolean {
  const na = toE164(a);
  const nb = toE164(b);
  if (!na || !nb) return false;
  return na === nb;
}

/**
 * National display mask. Strips +55 / 55 country prefix if present, then
 * formats the trailing 10 or 11 digits as (DD) XXXXX-XXXX (mobile) or
 * (DD) XXXX-XXXX (fixo). Partial input keeps partial mask — never blocks
 * typing.
 *
 * maskPhone("11922220001")       → "(11) 92222-0001"
 * maskPhone("+5511922220001")    → "(11) 92222-0001"
 * maskPhone("1133334444")        → "(11) 3333-4444"
 * maskPhone("11")                → "(11) "  → trim → "(11)"
 * maskPhone("")                  → ""
 */
export function maskPhone(value: string): string {
  let digits = cleanPhone(value);
  // Strip country prefix when present so the display is the national part.
  if (digits.startsWith('55') && digits.length > 11) {
    digits = digits.slice(2);
  }
  digits = digits.slice(0, 11);

  if (digits.length === 0) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

/**
 * Returns true if the national portion has 10 (fixo) or 11 (mobile) digits.
 * Country code prefix is optional. Does NOT validate DDD whitelist — that's
 * left to a separate region check if needed.
 *
 * isValidPhone("11922220001")    → true
 * isValidPhone("+5511922220001") → true
 * isValidPhone("119222")         → false
 * isValidPhone("")               → false
 */
export function isValidPhone(value: string): boolean {
  let digits = cleanPhone(value);
  if (digits.startsWith('55') && digits.length > 11) {
    digits = digits.slice(2);
  }
  return digits.length === 10 || digits.length === 11;
}
